import { type KeyboardEvent, type ReactNode, useMemo } from "react";
import CarouselImport from "react-multi-carousel";

import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { DefaultImageRenderer } from "@/src/components/atoms/DefaultImageRenderer";
import UnifiedImageCarouselView from "@/src/components/organisms/UnifiedImageCarousel/UnifiedImageCarouselView";

import "react-multi-carousel/lib/styles.css";

// Vite 8's rolldown dep pre-bundler exposes this CJS module's default export as
// the module namespace object ({ __esModule: true, default: Carousel }) instead
// of unwrapping it, so a plain default import resolves to an object rather than
// the component. Normalise it here to stay correct across dev (rolldown) and
// prod (rollup) builds.
const Carousel = ((CarouselImport as { default?: typeof CarouselImport })
  .default ?? CarouselImport) as typeof CarouselImport;

import UnifiedLightbox from "@/src/components/organisms/UnifiedImageCarousel/UnifiedLightbox";

import useProgressiveImageLoader from "@/src/hooks/useProgressiveImageLoader";
import { useUnifiedCarouselController } from "@/src/hooks/useUnifiedCarouselController";
import { UnifiedCarouselImage } from "@/src/types/carousel";

interface UnifiedImageCarouselProps {
  images: UnifiedCarouselImage[];
  initialIndex?: number;
  loop?: boolean;
  autoplay?: boolean;
  autoplayIntervalMs?: number;
  pauseOnHover?: boolean;
  arrowVisibility?: "always" | "never" | "onHover";
  leftArrowIcon?: ReactNode;
  rightArrowIcon?: ReactNode;
  showCounter?: boolean;
  counterPosition?: "top" | "bottom";
  showThumbnails?: boolean;
  enableLightbox?: boolean;
  lightboxShowCounter?: boolean;
  lightboxCounterPosition?: "top" | "bottom";
  lightboxShowThumbnails?: boolean;
  lightboxShowRotateButton?: boolean;
  lightboxZoomEnabled?: boolean;
  lightboxZoomFactor?: number;
  lightboxLensSize?: number;
  showAdditionalInfoBadge?: boolean;
  onIndexChange?: (index: number) => void;
  onLightboxOpen?: (index: number) => void;
  onLightboxClose?: () => void;
  renderImage?: (image: UnifiedCarouselImage, index: number) => ReactNode;
  responsive?: {
    desktop?: { breakpoint: { max: number; min: number }; items: number };
    tablet?: { breakpoint: { max: number; min: number }; items: number };
    mobile?: { breakpoint: { max: number; min: number }; items: number };
  };
  sx?: SxProps;
  containerSx?: SxProps;
  maxWidth?: string | number;
  height?: string | number;
  // Horizontal padding (in px) applied to each side of a slide. The visible
  // gap between two adjacent cards equals `2 * slideGutterPx`. Defaults to 4.
  slideGutterPx?: number;
}

const defaultRenderImage = (
  image: UnifiedCarouselImage,
  index: number,
  height?: string | number
): ReactNode => {
  return <DefaultImageRenderer image={image} index={index} height={height} />;
};

const UnifiedImageCarousel = (props: UnifiedImageCarouselProps) => {
  const {
    images,
    initialIndex = 0,
    loop = true,
    autoplay = false,
    autoplayIntervalMs = 5000,
    pauseOnHover = false,
    arrowVisibility = "always",
    leftArrowIcon,
    rightArrowIcon,
    showCounter = false,
    counterPosition = "bottom",
    showThumbnails = false,
    enableLightbox = false,
    lightboxShowCounter = true,
    lightboxCounterPosition = "bottom",
    lightboxShowThumbnails = false,
    lightboxShowRotateButton = false,
    lightboxZoomEnabled = true,
    lightboxZoomFactor = 1,
    lightboxLensSize = 200,
    showAdditionalInfoBadge = false,
    onIndexChange,
    onLightboxOpen,
    onLightboxClose,
    renderImage,
    responsive,
    sx,
    containerSx,
    maxWidth,
    height,
    slideGutterPx: slideGutterPxRaw = 4
  } = props;

  const slideGutterPx = Number.isFinite(slideGutterPxRaw)
    ? Math.max(0, slideGutterPxRaw)
    : 4;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Only use default height if height prop is not provided
  // This allows consumer home page to use auto height while dealer page can use fixed height
  const responsiveHeight = height ?? undefined;

  const controller = useUnifiedCarouselController({
    images,
    initialIndex,
    loop,
    autoplay,
    autoplayIntervalMs,
    pauseOnHover,
    onIndexChange
  });

  const { imageLoadStatus, priorityLoadOriginal } =
    useProgressiveImageLoader(images);

  const handleMouseEnter = () => {
    if (pauseOnHover) {
      controller.pauseAutoplay();
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      controller.resumeAutoplay();
    }
  };

  const handleImageClick = () => {
    if (enableLightbox) {
      controller.openLightbox();
      onLightboxOpen?.(controller.currentIndex);
    }
  };

  const responsiveConfig = useMemo(() => {
    if (responsive) {
      return {
        desktop: responsive.desktop ?? {
          breakpoint: { max: 3000, min: 1375 },
          items: 1
        },
        tablet: responsive.tablet ?? {
          breakpoint: { max: 1374, min: 900 },
          items: 1
        },
        mobile: responsive.mobile ?? {
          breakpoint: { max: 899, min: 0 },
          items: 1
        }
      };
    }
    return undefined;
  }, [responsive]);

  // Create a wrapper for renderImage that includes height for default renderer
  const imageRenderer = useMemo(() => {
    // Check if we're using the default renderer (either undefined prop or same reference)
    // Since we define defaultRenderImage locally, we can't compare references easily if passed as prop default
    // But if props.renderImage is undefined, we use our default.
    if (!renderImage) {
      return (img: UnifiedCarouselImage, idx: number) =>
        defaultRenderImage(img, idx, responsiveHeight);
    }
    return renderImage;
  }, [renderImage, responsiveHeight]);

  // For lightbox, pass undefined if we are using default renderer so lightbox uses its own default
  const lightboxRenderImage = useMemo(() => {
    if (!renderImage) {
      return undefined;
    }
    return renderImage;
  }, [renderImage]);

  // For now, use react-multi-carousel only when responsive config is provided
  if (responsiveConfig && autoplay) {
    return (
      <>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: maxWidth ?? "100%",
            margin: "0 auto",
            ...(responsiveHeight && { height: responsiveHeight }),
            "& .react-multi-carousel-list": {
              padding: 0,
              margin: 0
            },
            "& .react-multi-carousel-track": {
              display: "flex",
              alignItems: "center"
            },
            "& .react-multi-carousel-item": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            ...containerSx
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Carousel
            responsive={responsiveConfig}
            infinite={loop}
            autoPlay={autoplay}
            autoPlaySpeed={autoplayIntervalMs}
            pauseOnHover={pauseOnHover}
            arrows={arrowVisibility !== "never"}
            showDots={false}
          >
            {images.map((image, index) => (
              <Box
                key={image.id}
                sx={{
                  width: "100%",
                  padding: `0 ${slideGutterPx}px`,
                  boxSizing: "border-box",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                {imageRenderer(image, index)}
              </Box>
            ))}
          </Carousel>

          {showCounter && images.length > 1 && (
            <Box
              onClick={enableLightbox ? handleImageClick : undefined}
              onKeyDown={
                enableLightbox
                  ? (event: KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleImageClick();
                      }
                    }
                  : undefined
              }
              role={enableLightbox ? "button" : undefined}
              tabIndex={enableLightbox ? 0 : undefined}
              sx={{
                position: "absolute",
                ...(counterPosition === "top"
                  ? { top: "3px" }
                  : { bottom: "3px" }),
                left: 0,
                color: theme.palette.common.white,
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: theme.typography.fontWeightMedium,
                zIndex: 25,
                backgroundColor: theme.palette.misc.backdropOverlay,
                padding: isMobile ? "4px 8px" : "6px 10px",
                borderRadius: "4px",
                minWidth: isMobile ? "50px" : "70px",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...(enableLightbox && {
                  cursor: "pointer",
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.semantic.info.main}`,
                    outlineOffset: "2px"
                  }
                })
              }}
            >
              {controller.currentIndex + 1}/{images.length}
            </Box>
          )}
        </Box>
        {enableLightbox && (
          <UnifiedLightbox
            images={images}
            open={controller.isLightboxOpen}
            startIndex={controller.lightboxIndex}
            onClose={() => {
              controller.closeLightbox();
              onLightboxClose?.();
            }}
            onIndexChange={(index) => {
              controller.setLightboxIndex(index);
              controller.goToIndex(index);
            }}
            showCounter={lightboxShowCounter}
            counterPosition={lightboxCounterPosition}
            showThumbnails={lightboxShowThumbnails}
            showRotateButton={lightboxShowRotateButton}
            zoomEnabled={lightboxZoomEnabled}
            zoomFactor={lightboxZoomFactor}
            lensSize={lightboxLensSize}
            showAdditionalInfoBadge={showAdditionalInfoBadge}
            renderImage={lightboxRenderImage}
            imageLoadStatus={imageLoadStatus}
            onPriorityLoadOriginal={priorityLoadOriginal}
          />
        )}
      </>
    );
  }

  // Use custom single-image view for non-autoplay, single-image scenarios
  return (
    <>
      <UnifiedImageCarouselView
        images={images}
        currentIndex={controller.currentIndex}
        onNext={controller.goNext}
        onPrevious={controller.goPrevious}
        onImageClick={enableLightbox ? handleImageClick : undefined}
        arrowVisibility={arrowVisibility}
        leftArrowIcon={leftArrowIcon}
        rightArrowIcon={rightArrowIcon}
        showCounter={showCounter}
        counterPosition={counterPosition}
        showThumbnails={showThumbnails}
        onThumbnailClick={(index) => controller.goToIndex(index)}
        renderImage={imageRenderer}
        showAdditionalInfoBadge={showAdditionalInfoBadge}
        sx={sx}
        containerSx={containerSx}
        maxWidth={maxWidth}
        height={responsiveHeight}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      {enableLightbox && (
        <UnifiedLightbox
          images={images}
          open={controller.isLightboxOpen}
          startIndex={controller.lightboxIndex}
          onClose={() => {
            controller.closeLightbox();
            onLightboxClose?.();
          }}
          onIndexChange={(index) => {
            controller.setLightboxIndex(index);
            controller.goToIndex(index);
          }}
          showCounter={lightboxShowCounter}
          counterPosition={lightboxCounterPosition}
          showThumbnails={lightboxShowThumbnails}
          showRotateButton={lightboxShowRotateButton}
          zoomEnabled={lightboxZoomEnabled}
          zoomFactor={lightboxZoomFactor}
          lensSize={lightboxLensSize}
          showAdditionalInfoBadge={showAdditionalInfoBadge}
          renderImage={lightboxRenderImage}
          imageLoadStatus={imageLoadStatus}
          onPriorityLoadOriginal={priorityLoadOriginal}
        />
      )}
    </>
  );
};

export default UnifiedImageCarousel;
